<template>
  <div class="referral-program-members">
    <HeadSection
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
    />
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection" :isBlockStyle="true">
      <div class="tabs-container">
        <a-tabs
          v-model="activeTab"
          type="card"
          tab-position="top"
        >
          <a-tab-pane
            v-for="tab in tabs"
            :key="tab.key"
            force-render
          >
            <template #tab>
              <DefaultLabelTab
                :name="tab.name"
              />
            </template>
            <MemberInfoSkeleton v-if="loading" />
            <component
              v-else-if="member"
              :is="tab.component"
              :member="member"
              :program="program"
              :createPermission="createPermission"
            />
            <div v-else class="d-flex flex-column justify-content-center align-items-center h-full">
              <span class="d-block font-weight-bold font-size-18 mb-2">User not found</span>
              <div class="font-size-16">
                Back to <router-link :to="`/referral/programs/${programId}/members`" class="text-primary">members table</router-link>
              </div>
            </div>
          </a-tab-pane>
        </a-tabs>
      </div>
    </MainContainer>
    <div v-if="deletePermission" class="mt-3">
      <div
        class="d-flex align-items-center text-danger cursor-pointer hover-underline"
        style="width: fit-content;"
        @click="toggleShowRemoveUserModal(true)"
      >
        <a-icon type="delete" class="mr-2" />
        <span>Remove from referral program</span>
      </div>
    </div>
    <RemoveUserModal
      :show="showRemoveUserModal"
      @close="toggleShowRemoveUserModal"
      @remove="removeFromProgram"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import DefaultLabelTab from '@/views/apps/referral-program/components/DefaultLabelTab.vue'
import MemberInfoSkeleton from '../components/MemberInfoSkeleton.vue'
import MemberInfo from '../components/MemberInfo.vue'
import UserBetsHistory from '../components/UserBetsHistory.vue'
import RemoveUserModal from '@/views/apps/referral-program/referral-programs/members/components/RemoveUserModal.vue'
import { getProgramParams } from '@/views/apps/referral-program/referral-programs/composables/useGetProgramParams.js'
import { orderConditions } from '@/views/apps/referral-program/referral-programs/composables/useOrderConditions.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    DefaultLabelTab,
    MemberInfoSkeleton,
    MemberInfo,
    UserBetsHistory,
    RemoveUserModal,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    program: null,
    activeTab: 1,
    tabs: [
      { name: 'General', key: 1, component: MemberInfo },
      { name: 'Bets history', key: 2, component: UserBetsHistory },
    ],
    member: null,
    showRemoveUserModal: false,
    loading: true,
  }),
  watch: {
    memberId: {
      handler(val, oldVal) {
        if (val === oldVal) {
          return
        }
        this.getInfo()
      },
      deep: true,
    },
  },
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    innerProgramName() {
      return this.program?.name || ''
    },
    programId() {
      return this.$route.params.id
    },
    roleSystemName() {
      return this.$route.params.role
    },
    memberId() {
      return this.$route.params.member_id
    },
    breadcrumbs() {
      return [
        { name: 'Referral programs', link: '/referral/programs' },
        { name: this.innerProgramName, link: `/referral/programs/${this.program?.id}` },
        { name: 'Members', link: `/referral/programs/${this.program?.id}/members` },
        { name: this.memberId, link: null },
      ]
    },
  },
  methods: {
    toggleShowRemoveUserModal(status = false) {
      this.showRemoveUserModal = status
    },
    removeFromProgram() {},
    async getMemberInfo() {
      try {
        const url = `${referralApiUrl}/referral-programs/${this.programId}/members/${this.roleSystemName}/${this.memberId}`
        const response = await apiClient.get(url)
        const { data } = response?.data
        if (data) {
          const orderedConditions = data?.conditions?.sort((a, b) => orderConditions().indexOf(a.type) - orderConditions().indexOf(b.type))
          this.member = { ...data, conditions: orderedConditions }
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      }
    },
    async getProgramParams() {
      const data = await getProgramParams(this.programId)
      if (data) {
        this.program = data
        return
      }
      this.$router.push('/referral/programs')
    },
    async getInfo() {
      this.loading = true

      const requests = [this.getProgramParams(), this.getMemberInfo()]
      await Promise.all(requests)
      this.$nextTick(() => {
        this.loading = false
      })
    },
  },
  mounted() {
    this.getInfo()
  },
}
</script>

<style lang="scss">
.referral-program-members {
  display: flex;
  flex-direction: column;
  .ant-table-row {
    cursor: pointer;
  }
  .tabs-container {
    overflow: hidden;
    height: 100%;
  }
  .tabs-container > .ant-tabs {
    height: 100%;
  }
  .tabs-container > .ant-tabs-card > .ant-tabs-content {
    height: 100%;
    margin-top: -16px;
  }

  .tabs-container > .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane {
    background: #fff !important;
    padding: 16px;
  }
  .tabs-container > .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane-active {
    height: 100%;
  }
  .tabs-container > .ant-tabs-card > .ant-tabs-bar {
    border-color: #fff !important;
  }

  .tabs-container > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab {
    border-color: transparent;
    background: transparent;
    margin-right: 0 !important;
  }

  .tabs-container > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab-active {
    border-color: #fff !important;
    background: #fff !important;
  }
}
</style>
